import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Tooltip, Button } from 'antd';
import { ZoomInOutlined, ZoomOutOutlined, ColumnWidthOutlined } from '@ant-design/icons';

const MIN_SFX_DURATION_SEC = 0.25;
const SFX_RESIZE_HANDLE_PX = 7;

interface TimelinePanel {
  panelId: string;
  orderIndex: number;
  startTime: number;
  endTime: number;
  imageUrl?: string;
  caption?: string;
  motionEffects?: any[];
  soundEffects?: any[];
}

interface ProfessionalTimelineProps {
  panels: TimelinePanel[];
  totalDuration: number;
  currentTime: number;
  selectedPanelId: string | null;
  onPanelSelect: (panelId: string) => void;
  onSeek: (time: number) => void;
  backgroundMusicUrl?: string;
  onMotionSelect?: (panelId: string, motionIndex: number) => void;
  onSoundSelect?: (panelId: string, soundIndex: number) => void;
  /** Kéo / resize SFX trên timeline — cập nhật triggerTime (theo panel đích) và duration */
  onSfxTimelineUpdate?: (update: {
    fromPanelId: string;
    sfxIndex: number;
    sfxId?: string;
    triggerTime: number;
    duration: number;
    targetPanelId: string;
  }) => void;
}

function sortPanelsByTime(panels: TimelinePanel[]): TimelinePanel[] {
  return [...panels].sort((a, b) => a.startTime - b.startTime);
}

/** Panel chứa thời điểm t (ưu tiên [start, end)); nếu lệch khe thì gán panel gần nhất */
function findPanelForGlobalTime(panels: TimelinePanel[], t: number): TimelinePanel | null {
  const sorted = sortPanelsByTime(panels);
  if (sorted.length === 0) return null;
  for (const p of sorted) {
    if (t >= p.startTime && t < p.endTime) return p;
  }
  if (t < sorted[0].startTime) return sorted[0];
  const last = sorted[sorted.length - 1];
  if (t >= last.endTime) return last;
  let best = sorted[0];
  let bestDist = Infinity;
  for (const p of sorted) {
    if (t >= p.startTime && t <= p.endTime) return p;
    const d = Math.min(Math.abs(t - p.startTime), Math.abs(t - p.endTime));
    if (d < bestDist) {
      bestDist = d;
      best = p;
    }
  }
  return best;
}

function clampSfxToPanel(
  panel: TimelinePanel,
  globalStart: number,
  duration: number,
  minDur: number
): { triggerTime: number; duration: number } {
  const pd = Math.max(0.001, panel.endTime - panel.startTime);
  let trigger = globalStart - panel.startTime;
  let dur = Math.max(minDur, duration);
  trigger = Math.max(0, Math.min(trigger, pd - minDur));
  if (trigger + dur > pd) {
    dur = Math.max(minDur, pd - trigger);
  }
  return { triggerTime: trigger, duration: dur };
}

export const ProfessionalTimeline: React.FC<ProfessionalTimelineProps> = ({
  panels,
  totalDuration,
  currentTime,
  selectedPanelId,
  onPanelSelect,
  onSeek,
  backgroundMusicUrl,
  onMotionSelect,
  onSoundSelect,
  onSfxTimelineUpdate,
}) => {
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [sfxDragActive, setSfxDragActive] = useState(false);
  const timelineRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  type SfxDragMode = 'move' | 'resize-left' | 'resize-right';
  type SfxDragSession = {
    mode: SfxDragMode;
    fromPanelId: string;
    sfxIndex: number;
    sfxId?: string;
    /** Panel cố định khi resize (không đổi panel) */
    anchorPanelId: string;
    initialGlobalStart: number;
    initialDuration: number;
    pointerId: number;
    startClientX: number;
    /** Vị trí hiện tại khi kéo (preview) */
    globalStart: number;
    duration: number;
    targetPanelId: string;
    /** Track if drag threshold reached */
    dragStarted: boolean;
  };
  const sfxDragRef = useRef<SfxDragSession | null>(null);
  const sfxPointerMoveInnerRef = useRef<(e: PointerEvent) => void>(() => {});
  const [, sfxDragTick] = useState(0);
  const bumpSfxPreview = useCallback(() => sfxDragTick((n) => n + 1), []);

  const sfxPointerMoveStable = useCallback((e: PointerEvent) => {
    sfxPointerMoveInnerRef.current(e);
  }, []);

  // Tính zoom "fit to screen": toàn bộ timeline vừa đúng container width
  const getFitZoom = useCallback(() => {
    const containerWidth = containerRef.current?.clientWidth ?? 800;
    return containerWidth / (totalDuration * 100);
  }, [totalDuration]);

  // Khi mount hoặc totalDuration thay đổi → set zoom về fit
  useEffect(() => {
    const fitZoom = getFitZoom();
    setZoom(fitZoom);
  }, [totalDuration, getFitZoom]);

  const pixelsPerSecond = 100 * zoom;
  const timelineWidth = Math.max(totalDuration * pixelsPerSecond, 1);

  const panelsRef = useRef(panels);
  panelsRef.current = panels;
  const totalDurationRef = useRef(totalDuration);
  totalDurationRef.current = totalDuration;
  const pixelsPerSecondRef = useRef(pixelsPerSecond);
  pixelsPerSecondRef.current = pixelsPerSecond;
  const onSfxTimelineUpdateRef = useRef(onSfxTimelineUpdate);
  onSfxTimelineUpdateRef.current = onSfxTimelineUpdate;

  const endSfxDrag = useCallback(() => {
    const s = sfxDragRef.current;
    sfxDragRef.current = null;
    // Only set dragging states if drag was actually started
    if (s?.dragStarted) {
      setSfxDragActive(false);
      setIsDragging(false);
      bumpSfxPreview();
    }
  }, [bumpSfxPreview]);

  const onSfxPointerUp = useCallback(
    (e: PointerEvent) => {
      const s = sfxDragRef.current;
      if (!s || e.pointerId !== s.pointerId) return;
      window.removeEventListener('pointermove', sfxPointerMoveStable);
      window.removeEventListener('pointerup', onSfxPointerUp);
      window.removeEventListener('pointercancel', onSfxPointerUp);

      // Only update if drag actually started (moved more than threshold)
      if (s.dragStarted) {
        const cb = onSfxTimelineUpdateRef.current;
        if (cb && s) {
          const panel = panelsRef.current.find((p) => p.panelId === s.targetPanelId);
          if (panel) {
            const clamped = clampSfxToPanel(panel, s.globalStart, s.duration, MIN_SFX_DURATION_SEC);
            cb({
              fromPanelId: s.fromPanelId,
              sfxIndex: s.sfxIndex,
              sfxId: s.sfxId,
              triggerTime: clamped.triggerTime,
              duration: clamped.duration,
              targetPanelId: s.targetPanelId,
            });
          }
        }
      }
      endSfxDrag();
    },
    [endSfxDrag, sfxPointerMoveStable]
  );

  sfxPointerMoveInnerRef.current = (e: PointerEvent) => {
    const s = sfxDragRef.current;
    if (!s || e.pointerId !== s.pointerId) return;
    
    const dragDistance = Math.abs(e.clientX - s.startClientX);
    const DRAG_THRESHOLD = 3; // pixels
    
    // Only start dragging if moved more than threshold
    if (!s.dragStarted && dragDistance < DRAG_THRESHOLD) {
      return;
    }
    
    if (!s.dragStarted) {
      s.dragStarted = true;
      setSfxDragActive(true);
      setIsDragging(true);
    }
    
    const pps = pixelsPerSecondRef.current;
    const td = totalDurationRef.current;
    const deltaSec = (e.clientX - s.startClientX) / pps;

    if (s.mode === 'move') {
      let globalStart = s.initialGlobalStart + deltaSec;
      const dur = s.initialDuration;
      globalStart = Math.max(0, Math.min(globalStart, Math.max(0, td - dur)));
      const panel = findPanelForGlobalTime(panelsRef.current, globalStart);
      if (!panel) return;
      const clamped = clampSfxToPanel(panel, globalStart, dur, MIN_SFX_DURATION_SEC);
      s.globalStart = panel.startTime + clamped.triggerTime;
      s.duration = clamped.duration;
      s.targetPanelId = panel.panelId;
    } else if (s.mode === 'resize-right') {
      const anchor = panelsRef.current.find((p) => p.panelId === s.anchorPanelId);
      if (!anchor) return;
      let newDur = Math.max(MIN_SFX_DURATION_SEC, s.initialDuration + deltaSec);
      const maxDur = anchor.endTime - s.initialGlobalStart;
      newDur = Math.max(MIN_SFX_DURATION_SEC, Math.min(newDur, maxDur));
      s.globalStart = s.initialGlobalStart;
      s.duration = newDur;
      s.targetPanelId = s.anchorPanelId;
    } else if (s.mode === 'resize-left') {
      const anchor = panelsRef.current.find((p) => p.panelId === s.anchorPanelId);
      if (!anchor) return;
      const globalEnd = s.initialGlobalStart + s.initialDuration;
      let newStart = s.initialGlobalStart + deltaSec;
      newStart = Math.max(anchor.startTime, Math.min(newStart, globalEnd - MIN_SFX_DURATION_SEC));
      s.globalStart = newStart;
      s.duration = globalEnd - newStart;
      s.targetPanelId = s.anchorPanelId;
    }
    bumpSfxPreview();
  };

  const beginSfxDrag = useCallback(
    (
      e: React.PointerEvent,
      mode: SfxDragMode,
      panel: TimelinePanel,
      sfx: any,
      sfxIndex: number
    ) => {
      if (!onSfxTimelineUpdateRef.current) return;
      e.stopPropagation();
      e.preventDefault();
      const globalStart = panel.startTime + (sfx.triggerTime || 0);
      const duration = sfx.duration || 1;
      sfxDragRef.current = {
        mode,
        fromPanelId: panel.panelId,
        sfxIndex,
        sfxId: sfx.id,
        anchorPanelId: panel.panelId,
        initialGlobalStart: globalStart,
        initialDuration: duration,
        pointerId: e.pointerId,
        globalStart,
        duration,
        targetPanelId: panel.panelId,
        startClientX: e.clientX,
        dragStarted: false,
      };
      window.addEventListener('pointermove', sfxPointerMoveStable);
      window.addEventListener('pointerup', onSfxPointerUp);
      window.addEventListener('pointercancel', onSfxPointerUp);
    },
    [onSfxPointerUp, sfxPointerMoveStable]
  );

  useEffect(() => {
    return () => {
      if (sfxDragRef.current) {
        window.removeEventListener('pointermove', sfxPointerMoveStable);
        window.removeEventListener('pointerup', onSfxPointerUp);
        window.removeEventListener('pointercancel', onSfxPointerUp);
      }
    };
  }, [sfxPointerMoveStable, onSfxPointerUp]);

  const handleTimelineClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).closest('[data-sfx-block]')) return;
      if (!timelineRef.current) return;

      const rect = timelineRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left + timelineRef.current.scrollLeft;
      const time = clickX / pixelsPerSecond;

      if (time >= 0 && time <= totalDuration) {
        onSeek(time);
      }
    },
    [pixelsPerSecond, totalDuration, onSeek]
  );

  const handleZoomIn = () => setZoom(prev => Math.min(prev * 1.5, 10));
  const handleZoomOut = () => {
    const fitZoom = getFitZoom();
    setZoom(prev => Math.max(prev / 1.5, fitZoom));
  };
  const handleFit = () => setZoom(getFitZoom());
  
  return (
    <div
      ref={containerRef}
      style={{ 
        backgroundColor: '#1a1a1a', 
        borderRadius: '8px',
        padding: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        height: '100%'
      }}
    >
      {/* Timeline Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ color: '#999', fontSize: '11px', fontWeight: 'bold' }}>
          📽️ TIMELINE
        </div>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <Tooltip title="Zoom out">
            <Button 
              size="small" 
              icon={<ZoomOutOutlined />} 
              onClick={handleZoomOut}
              disabled={zoom <= getFitZoom() + 0.001}
            />
          </Tooltip>
          <Tooltip title="Vừa màn hình">
            <Button
              size="small"
              icon={<ColumnWidthOutlined />}
              onClick={handleFit}
            />
          </Tooltip>
          <Tooltip title="Zoom in">
            <Button 
              size="small" 
              icon={<ZoomInOutlined />} 
              onClick={handleZoomIn}
              disabled={zoom >= 10}
            />
          </Tooltip>
          <span style={{ color: '#999', fontSize: '11px', padding: '0 8px', lineHeight: '24px', minWidth: 36, textAlign: 'right' }}>
            {Math.round(zoom * 100)}%
          </span>
        </div>
      </div>
      
      {/* Timeline Container */}
      <div
        ref={timelineRef}
        style={{
          flex: 1,
          overflowX: 'auto',
          overflowY: 'auto',
          position: 'relative',
          backgroundColor: '#0d0d0d',
          borderRadius: '4px',
          cursor: isDragging ? 'grabbing' : 'default',
        }}
        onClick={handleTimelineClick}
      >
        <div style={{ 
          position: 'relative', 
          width: `${timelineWidth}px`,
          minHeight: '250px'
        }}>
          {/* Time Ruler */}
          <div style={{ 
            height: '30px', 
            borderBottom: '1px solid #333',
            position: 'relative',
            backgroundColor: '#1a1a1a'
          }}>
            {Array.from({ length: Math.ceil(totalDuration) + 1 }).map((_, i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  left: `${i * pixelsPerSecond}px`,
                  top: 0,
                  height: '100%',
                  borderLeft: '1px solid #444',
                  paddingLeft: '4px',
                }}
              >
                <span style={{ fontSize: '10px', color: '#999' }}>
                  {i}s
                </span>
              </div>
            ))}
          </div>
          
          {/* Video Track */}
          <div style={{ 
            height: '120px',
            borderBottom: '1px solid #333',
            position: 'relative',
            backgroundColor: '#1a1a1a',
            padding: '4px 0',
            overflow: 'visible'
          }}>
            <div style={{ 
              fontSize: '10px', 
              color: '#999', 
              padding: '0 8px 2px',
              fontWeight: 'bold'
            }}>
              📹 VIDEO
            </div>
            <div style={{ position: 'relative', height: '106px' }}>
              {/* Panel Images */}
              {panels.map((panel, idx) => {
                const left = panel.startTime * pixelsPerSecond;
                const width = (panel.endTime - panel.startTime) * pixelsPerSecond;
                const isSelected = panel.panelId === selectedPanelId;
                const hasMotion = (panel.motionEffects?.length || 0) > 0;
                const hasSfx = (panel.soundEffects?.length || 0) > 0;
                
                return (
                  <Tooltip
                    key={panel.panelId}
                    title={
                      <div>
                        <div><strong>Panel {idx + 1}</strong></div>
                        <div>{panel.caption?.substring(0, 50)}</div>
                        <div>{panel.startTime.toFixed(1)}s - {panel.endTime.toFixed(1)}s</div>
                        {hasMotion && <div>🎬 {panel.motionEffects?.length} motion effects</div>}
                        {hasSfx && <div>🔊 {panel.soundEffects?.length} sound effects</div>}
                      </div>
                    }
                  >
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        onPanelSelect(panel.panelId);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.zIndex = '10';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.zIndex = '1';
                      }}
                      style={{
                        position: 'absolute',
                        left: `${left}px`,
                        width: `${width}px`,
                        height: '52px',
                        backgroundColor: '#000',
                        border: isSelected ? '2px solid #40a9ff' : '1px solid #555',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: isSelected ? '0 0 8px rgba(24, 144, 255, 0.6)' : '0 1px 3px rgba(0,0,0,0.3)',
                      }}
                    >
                      {panel.imageUrl ? (
                        <img
                          src={panel.imageUrl}
                          alt={`Panel ${idx + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            filter: 'brightness(1.1) contrast(1.1)',
                          }}
                        />
                      ) : (
                        <div style={{ color: '#666', fontSize: '24px' }}>🖼️</div>
                      )}
                      
                      {/* Panel number badge */}
                      <div style={{
                        position: 'absolute',
                        top: '2px',
                        left: '2px',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        color: '#fff',
                        padding: '1px 4px',
                        borderRadius: '2px',
                        fontSize: '9px',
                        fontWeight: 'bold',
                      }}>
                        #{idx + 1}
                      </div>
                      
                      {/* Duration badge */}
                      <div style={{
                        position: 'absolute',
                        bottom: '2px',
                        right: '2px',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        color: '#fff',
                        padding: '1px 4px',
                        borderRadius: '2px',
                        fontSize: '8px',
                      }}>
                        {(panel.endTime - panel.startTime).toFixed(1)}s
                      </div>
                    </div>
                  </Tooltip>
                );
              })}
              
              {/* Motion Effects Layer - separate from panels */}
              {panels.map((panel, idx) => {
                if (!panel.motionEffects || panel.motionEffects.length === 0) return null;
                
                const panelLeft = panel.startTime * pixelsPerSecond;
                const panelWidth = (panel.endTime - panel.startTime) * pixelsPerSecond;
                const panelDuration = panel.endTime - panel.startTime;
                
                const motionTypeEmoji: Record<string, string> = {
                  'zoom-in': '🔍',
                  'zoom-out': '🔎',
                  'pan-left': '⬅️',
                  'pan-right': '➡️',
                  'pan-up': '⬆️',
                  'pan-down': '⬇️',
                  'ken-burns': '🎬',
                  'zoom-in-out': '🔄',
                  'double-zoom': '🎯',
                  'slow-pan': '🌊',
                  'static': '📱',
                  'slide': '💨',
                  'fade': '✨',
                  'shake': '🔔',
                  'rotate': '🔄',
                };
                
                // Calculate lanes for overlapping effects
                const lanes: Array<Array<{motion: any, motionIdx: number}>> = [];
                
                panel.motionEffects.forEach((motion: any, motionIdx: number) => {
                  const motionStart = motion.startOffset || 0;
                  const motionEnd = motionStart + (motion.duration || 1);
                  
                  // Find first lane where this motion doesn't overlap
                  let placed = false;
                  for (let laneIdx = 0; laneIdx < lanes.length; laneIdx++) {
                    const lane = lanes[laneIdx];
                    const hasOverlap = lane.some(item => {
                      const itemStart = item.motion.startOffset || 0;
                      const itemEnd = itemStart + (item.motion.duration || 1);
                      // Check if intervals overlap
                      return motionStart < itemEnd && motionEnd > itemStart;
                    });
                    
                    if (!hasOverlap) {
                      lane.push({ motion, motionIdx });
                      placed = true;
                      break;
                    }
                  }
                  
                  if (!placed) {
                    lanes.push([{ motion, motionIdx }]);
                  }
                });
                
                const laneHeight = 18;
                const totalHeight = lanes.length * laneHeight;
                
                return (
                  <div 
                    key={`${panel.panelId}-motions`}
                    style={{ 
                      position: 'absolute', 
                      top: '54px', 
                      left: `${panelLeft}px`,
                      width: `${panelWidth}px`,
                      height: `${totalHeight}px`,
                      pointerEvents: 'none',
                      zIndex: 15,
                    }}
                  >
                    {lanes.map((lane, laneIdx) => 
                      lane.map(({ motion, motionIdx }) => {
                        const motionStart = motion.startOffset || 0;
                        const motionDuration = motion.duration || 1;
                        const motionLeft = (motionStart / panelDuration) * panelWidth;
                        const motionWidth = Math.max((motionDuration / panelDuration) * panelWidth, 20);
                        const top = laneIdx * laneHeight;
                        
                        return (
                          <Tooltip
                            key={`${panel.panelId}-motion-${motionIdx}`}
                            title={`${motionTypeEmoji[motion.type] || '🎬'} ${motion.type} (${motionDuration.toFixed(1)}s)`}
                          >
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                onMotionSelect?.(panel.panelId, motionIdx);
                              }}
                              style={{
                                position: 'absolute',
                                left: `${motionLeft}px`,
                                top: `${top}px`,
                                width: `${motionWidth}px`,
                                height: `${laneHeight - 2}px`,
                                backgroundColor: '#722ed1',
                                border: '1px solid #9254de',
                                borderRadius: '2px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                transition: 'all 0.2s',
                                pointerEvents: 'auto',
                                zIndex: 15,
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#9254de';
                                e.currentTarget.style.transform = 'scale(1.1)';
                                e.currentTarget.style.zIndex = '25';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#722ed1';
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.zIndex = '15';
                              }}
                            >
                              {motionTypeEmoji[motion.type] || '🎬'}
                            </div>
                          </Tooltip>
                        );
                      })
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* BGM Track */}
          {backgroundMusicUrl && (
            <div style={{ 
              height: '30px',
              borderBottom: '1px solid #333',
              position: 'relative',
              backgroundColor: '#1a1a1a',
              padding: '2px 0'
            }}>
              <div style={{ 
                fontSize: '9px', 
                color: '#999', 
                padding: '0 8px 2px',
                fontWeight: 'bold'
              }}>
                🎵 BGM
              </div>
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: '16px',
                  width: `${totalDuration * pixelsPerSecond}px`,
                  height: '14px',
                  backgroundColor: '#1e5a3e',
                  border: '1px solid #2a7a4e',
                  borderRadius: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: '6px',
                }}
              >
                <span style={{ fontSize: '9px', color: '#52c41a' }}>
                  🎵 BGM
                </span>
              </div>
            </div>
          )}
          
          {/* SFX Track */}
          <div style={{ 
            minHeight: '40px',
            borderBottom: '1px solid #333',
            position: 'relative',
            backgroundColor: '#1a1a1a',
            padding: '2px 0'
          }}>
            <div style={{ 
              fontSize: '9px', 
              color: '#999', 
              padding: '0 8px 2px',
              fontWeight: 'bold'
            }}>
              🔊 SFX
            </div>
            <div
              style={{ position: 'relative', minHeight: '28px', userSelect: sfxDragActive ? 'none' : undefined }}
            >
              {(() => {
                // Calculate lanes for all SFX to avoid overlap
                const allSfx: Array<{
                  panel: TimelinePanel;
                  sfx: any;
                  sfxIdx: number;
                  globalStart: number;
                  globalEnd: number;
                }> = [];
                
                panels.forEach(panel => {
                  panel.soundEffects?.forEach((sfx: any, sfxIdx: number) => {
                    const d = sfxDragRef.current;
                    const draggingThis = !!d && d.fromPanelId === panel.panelId && d.sfxIndex === sfxIdx;
                    const globalStart = draggingThis ? d!.globalStart : panel.startTime + (sfx.triggerTime || 0);
                    const duration = draggingThis ? d!.duration : sfx.duration || 1;
                    const globalEnd = globalStart + duration;
                    
                    allSfx.push({ panel, sfx, sfxIdx, globalStart, globalEnd });
                  });
                });
                
                // Assign lanes to avoid overlap
                const lanes: Array<Array<typeof allSfx[0]>> = [];
                const sfxLaneMap = new Map<string, number>();
                
                allSfx.forEach(item => {
                  const key = `${item.panel.panelId}-${item.sfxIdx}`;
                  let placed = false;
                  
                  for (let laneIdx = 0; laneIdx < lanes.length; laneIdx++) {
                    const lane = lanes[laneIdx];
                    const hasOverlap = lane.some(other => {
                      return item.globalStart < other.globalEnd && item.globalEnd > other.globalStart;
                    });
                    
                    if (!hasOverlap) {
                      lane.push(item);
                      sfxLaneMap.set(key, laneIdx);
                      placed = true;
                      break;
                    }
                  }
                  
                  if (!placed) {
                    lanes.push([item]);
                    sfxLaneMap.set(key, lanes.length - 1);
                  }
                });
                
                const laneHeight = 24;
                const totalHeight = Math.max(28, lanes.length * laneHeight + 4);
                
                return (
                  <>
                    <div style={{ height: `${totalHeight}px`, position: 'relative' }}>
                      {allSfx.map(({ panel, sfx, sfxIdx, globalStart, globalEnd }) => {
                        const key = `${panel.panelId}-${sfxIdx}`;
                        const laneIdx = sfxLaneMap.get(key) || 0;
                        const d = sfxDragRef.current;
                        const draggingThis = !!d && d.fromPanelId === panel.panelId && d.sfxIndex === sfxIdx;
                        const duration = globalEnd - globalStart;
                        const left = globalStart * pixelsPerSecond;
                        const width = Math.max(duration * pixelsPerSecond, 16);
                        const top = laneIdx * laneHeight;
                        const canEditSfx = Boolean(onSfxTimelineUpdate);

                        return (
                          <Tooltip
                            key={key}
                            open={draggingThis ? false : undefined}
                            title={
                              <div>
                                <div>
                                  <strong>{sfx.audioName || 'SFX'}</strong>
                                </div>
                                <div>
                                  {globalStart.toFixed(1)}s → {globalEnd.toFixed(1)}s
                                </div>
                                <div>Volume: {Math.round((sfx.volume || 0.5) * 100)}%</div>
                                {canEditSfx && (
                                  <div style={{ marginTop: 4, opacity: 0.85 }}>
                                    Kéo giữa: di chuyển · Hai mép: kéo dài/ngắn
                                  </div>
                                )}
                              </div>
                            }
                          >
                            <div
                              data-sfx-block
                              style={{
                                position: 'absolute',
                                left: `${left}px`,
                                top: `${top}px`,
                                width: `${width}px`,
                                height: `${laneHeight - 2}px`,
                                backgroundColor: draggingThis ? '#faad14' : '#d4b106',
                                border: draggingThis ? '1px solid #ffc53d' : '1px solid #faad14',
                                borderRadius: '2px',
                                display: 'flex',
                                alignItems: 'stretch',
                                fontSize: '9px',
                                color: '#000',
                                fontWeight: 'bold',
                                overflow: 'hidden',
                                transition: draggingThis ? 'none' : 'background-color 0.15s, border-color 0.15s',
                                zIndex: draggingThis ? 40 : 10,
                                touchAction: 'none',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                onSoundSelect?.(panel.panelId, sfxIdx);
                              }}
                              onPointerDown={(e) => e.stopPropagation()}
                            >
                              {canEditSfx && (
                                <div
                                  role="presentation"
                                  onPointerDown={(e) => beginSfxDrag(e, 'resize-left', panel, sfx, sfxIdx)}
                                  style={{
                                    width: SFX_RESIZE_HANDLE_PX,
                                    flexShrink: 0,
                                    cursor: 'ew-resize',
                                    background: 'linear-gradient(90deg, rgba(0,0,0,0.2), transparent)',
                                  }}
                                />
                              )}
                              <div
                                role="presentation"
                                onPointerDown={
                                  canEditSfx
                                    ? (e) => beginSfxDrag(e, 'move', panel, sfx, sfxIdx)
                                    : undefined
                                }
                                style={{
                                  flex: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: canEditSfx ? 'grab' : 'pointer',
                                  minWidth: 0,
                                }}
                              >
                                🔊
                              </div>
                              {canEditSfx && (
                                <div
                                  role="presentation"
                                  onPointerDown={(e) => beginSfxDrag(e, 'resize-right', panel, sfx, sfxIdx)}
                                  style={{
                                    width: SFX_RESIZE_HANDLE_PX,
                                    flexShrink: 0,
                                    cursor: 'ew-resize',
                                    background: 'linear-gradient(270deg, rgba(0,0,0,0.2), transparent)',
                                  }}
                                />
                              )}
                            </div>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
          
          {/* Playhead */}
          <div
            style={{
              position: 'absolute',
              left: `${currentTime * pixelsPerSecond}px`,
              top: 0,
              bottom: 0,
              width: '2px',
              backgroundColor: '#ff4d4f',
              pointerEvents: 'none',
              zIndex: 100,
            }}
          >
            <div style={{
              position: 'absolute',
              top: '-4px',
              left: '-6px',
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '8px solid #ff4d4f',
            }} />
          </div>
        </div>
      </div>
    </div>
  );
};
